import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import type { AiGrantAccess, DrawingAccessRole, SceneMode } from "@excalidraw-ms/shared";
import { Button } from "@excalidraw-ms/shared-ui";
import DestructiveConfirmationDialog from "./DestructiveConfirmationDialog";
import { STOP_SESSION_COPY, type StopSessionCase, type StopSessionChoice } from "./stopSessionCopy";

interface CollabShareDialogProps {
  variant?: "editor" | "gallery";
  drawingTitle?: string;
  accessLoading?: boolean;
  accessError?: string | null;
  onRetryAccess?: () => void;
  peopleAccessLoading?: boolean;
  sharedWithPeople?: boolean;
  peopleSharingKind?: "none" | "link" | "recipient";
  peopleAccessError?: string | null;
  onRetryPeopleAccess?: () => void;
  peopleAccessRetryLabel?: string;
  aiAccessLoading?: boolean;
  aiAccessError?: string | null;
  onRetryAiAccess?: () => void;
  sceneMode: SceneMode;
  isCollaborating: boolean;
  userName: string;
  shareUrl: string | null;
  linkKind?: "sharing" | "existing-access";
  canCreateShareLink?: boolean;
  hasReusablePeopleLink?: boolean;
  linkRole?: DrawingAccessRole;
  linkBusy?: boolean;
  linkError?: string | null;
  /** Which stop-session outcome applies — see stopSessionCopy.ts. */
  stopCase?: StopSessionCase;
  onLinkRoleChange?: (role: DrawingAccessRole) => void;
  onStartSession: () => Promise<void>;
  startSessionDisabled?: boolean;
  startSessionDisabledReason?: string;
  onStopSession: (choice: StopSessionChoice) => Promise<void> | void;
  onCopyLink: () => Promise<void>;
  /** Optional cloud-sharing proposal; existing callers retain their current UX. */
  onShareWithAi?: () => Promise<void> | void;
  hasAiAccess?: boolean;
  aiCode?: string | null;
  aiConnectionStatus?: "connected" | "idle" | "not-connected" | "unconfirmed";
  aiAccess?: AiGrantAccess;
  aiLoading?: boolean;
  aiPreparing?: boolean;
  aiError?: string | null;
  onRequestStopAiAccess?: () => void;
  onConnectAiAssistant?: () => void;
  /** Offered on a browser-only drawing, which an assistant cannot open until it is saved. */
  onSaveToCloudForAi?: () => void;
  saveToCloudForAiDisabled?: boolean;
  saveToCloudForAiReason?: string;
  onClose: () => void;
}

const MAX_PROMPT_TITLE_LENGTH = 60;

function getAiPermissionCopy(aiAccess: AiGrantAccess | undefined): {
  badge: string;
  sentence: string;
} {
  if (!aiAccess) {
    return {
      badge: "Your access",
      sentence:
        "An assistant with this code can do exactly what you can do with this drawing — no more.",
    };
  }
  if (aiAccess.canEdit) {
    return {
      badge: "Can edit",
      sentence:
        "You can edit this drawing, so an assistant with this code can read it and save changes to it.",
    };
  }
  return {
    badge: "Can view",
    sentence:
      "You can only view this drawing, so an assistant with this code can read it but cannot change it.",
  };
}

function buildAiPrompt(title: string | undefined, code: string): string {
  const cleaned = (title ?? "")
    .replace(/["\r\n]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const codePoints = Array.from(cleaned);
  const shortened =
    codePoints.length > MAX_PROMPT_TITLE_LENGTH
      ? `${codePoints.slice(0, MAX_PROMPT_TITLE_LENGTH - 1).join("")}…`
      : cleaned;
  const subject = shortened ? `my drawing "${shortened}"` : "my drawing";
  return `Use the Excalidraw-MS tools to open ${subject} with code ${code}, then `;
}

export default function CollabShareDialog({
  variant = "editor",
  drawingTitle,
  accessLoading = false,
  accessError,
  onRetryAccess,
  peopleAccessLoading = false,
  sharedWithPeople,
  peopleSharingKind,
  peopleAccessError,
  onRetryPeopleAccess,
  peopleAccessRetryLabel = "Retry people access",
  aiAccessLoading = false,
  aiAccessError,
  onRetryAiAccess,
  sceneMode,
  isCollaborating,
  shareUrl,
  linkKind = "sharing",
  canCreateShareLink = true,
  hasReusablePeopleLink = !!shareUrl,
  linkRole = "editor",
  linkBusy = false,
  linkError,
  stopCase = sceneMode === "file-backed" ? "cloud-copy" : "local-keep",
  onLinkRoleChange,
  onStartSession,
  startSessionDisabled = false,
  startSessionDisabledReason,
  onStopSession,
  onCopyLink,
  onShareWithAi,
  hasAiAccess = false,
  aiCode = null,
  aiConnectionStatus = "not-connected",
  aiAccess,
  aiLoading = false,
  aiPreparing = false,
  aiError,
  onRequestStopAiAccess,
  onConnectAiAssistant,
  onSaveToCloudForAi,
  saveToCloudForAiDisabled = false,
  saveToCloudForAiReason,
  onClose,
}: CollabShareDialogProps) {
  const isGallery = variant === "gallery";
  const showDrawingIdentity = isGallery || sceneMode === "file-backed";
  const dialogTitle = showDrawingIdentity ? "Share drawing" : "Live collaboration";
  const isExistingAccessLink =
    sceneMode === "file-backed" && !canCreateShareLink && linkKind === "existing-access";
  const aiPrompt = aiCode ? buildAiPrompt(drawingTitle, aiCode) : null;
  const aiPermissionCopy = getAiPermissionCopy(aiAccess);
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isGallery) dialogRef.current?.focus();
  }, [isGallery]);
  const [actionState, setActionState] = useState<"idle" | "starting" | "stopping">("idle");
  const [startSettled, setStartSettled] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [stopError, setStopError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");
  const [aiCopyState, setAiCopyState] = useState<"idle" | "copied" | "error">("idle");
  const [startError, setStartError] = useState<string | null>(null);
  const [preparingAiRequest, setPreparingAi] = useState(false);
  const preparingAi = preparingAiRequest || aiPreparing;
  const [aiShareError, setAiShareError] = useState<string | null>(null);
  const aiRequestPending = useRef(false);
  const aiPromptRef = useRef<HTMLTextAreaElement>(null);
  const aiPermissionTooltipId = useId();
  const aiPermissionTooltipRef = useRef<HTMLSpanElement>(null);
  const [aiPermissionHovered, setAiPermissionHovered] = useState(false);
  const [aiPermissionFocused, setAiPermissionFocused] = useState(false);
  const [aiPermissionPinned, setAiPermissionPinned] = useState(false);
  const [aiPermissionDismissed, setAiPermissionDismissed] = useState(false);
  const aiPermissionTooltipOpen =
    !aiPermissionDismissed && (aiPermissionHovered || aiPermissionFocused || aiPermissionPinned);
  const supportsAiSharing = sceneMode === "file-backed" && !!onShareWithAi;
  const localChoice = !isGallery && sceneMode === "scene-only" && !!onSaveToCloudForAi;
  const hasActiveAiAccess = supportsAiSharing && (hasAiAccess || !!aiCode);
  const aiPromptVisible = hasActiveAiAccess || preparingAi;
  useEffect(() => {
    const el = aiPromptRef.current;
    if (!el) return;
    const resize = () => {
      el.style.height = "auto";
      const cs = getComputedStyle(el);
      const borderHeight = el.offsetHeight - el.clientHeight;
      const maxHeight =
        parseFloat(cs.lineHeight) * 2 +
        parseFloat(cs.paddingTop) +
        parseFloat(cs.paddingBottom) +
        borderHeight;
      el.style.height = `${Math.min(el.scrollHeight + borderHeight, maxHeight)}px`;
    };
    resize();
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      let observedWidth = el.getBoundingClientRect().width;
      observer = new ResizeObserver(([entry]) => {
        const width = entry?.contentRect.width ?? el.getBoundingClientRect().width;
        if (width === observedWidth) return;
        observedWidth = width;
        resize();
      });
      observer.observe(el);
    }
    return () => observer?.disconnect();
  }, [aiPrompt, aiPromptVisible]);
  useEffect(() => {
    if (!aiPermissionTooltipOpen) return;
    const dismissOutside = (event: PointerEvent) => {
      if (!aiPermissionTooltipRef.current?.contains(event.target as Node)) {
        setAiPermissionHovered(false);
        setAiPermissionFocused(false);
        setAiPermissionPinned(false);
        setAiPermissionDismissed(true);
      }
    };
    document.addEventListener("pointerdown", dismissOutside);
    return () => document.removeEventListener("pointerdown", dismissOutside);
  }, [aiPermissionTooltipOpen]);
  const showPeopleLink =
    (canCreateShareLink || isExistingAccessLink) &&
    (!supportsAiSharing || !!shareUrl || linkBusy || actionState === "starting");
  // Before a session is confirmed, hold every action until the initial people
  // and AI reads settle; otherwise the dialog flashes the in-session layout.
  const checkingEntryAccess =
    !isGallery &&
    !isCollaborating &&
    actionState !== "starting" &&
    !preparingAi &&
    (peopleAccessLoading || aiAccessLoading || aiLoading);
  // A share link can exist while the user has left the live session; the link
  // alone would otherwise be the only thing shown with no way to rejoin.
  const needsSessionRestart =
    !isGallery &&
    !isCollaborating &&
    (!!shareUrl || actionState === "starting") &&
    !checkingEntryAccess &&
    !peopleAccessLoading &&
    !peopleAccessError;
  const canPreparePeopleLink =
    canCreateShareLink &&
    !peopleAccessLoading &&
    !peopleAccessError &&
    (!sharedWithPeople || peopleSharingKind === "recipient" || hasReusablePeopleLink);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (aiPermissionTooltipOpen) {
        e.preventDefault();
        e.stopImmediatePropagation();
        setAiPermissionHovered(false);
        setAiPermissionFocused(false);
        setAiPermissionPinned(false);
        setAiPermissionDismissed(true);
        return;
      }
      if (showStopConfirm) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      onClose();
    },
    [aiPermissionTooltipOpen, onClose, showStopConfirm],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [handleKeyDown]);

  useEffect(() => {
    if (copyState === "idle") return;
    const timer = setTimeout(() => setCopyState("idle"), 2000);
    return () => clearTimeout(timer);
  }, [copyState]);

  useEffect(() => {
    if (aiCopyState === "idle") return;
    const timer = setTimeout(() => setAiCopyState("idle"), 2000);
    return () => clearTimeout(timer);
  }, [aiCopyState]);

  const sessionLabel = useMemo(() => {
    if (isGallery || supportsAiSharing) {
      if (actionState === "starting") return "Preparing link…";
      return "Share with people";
    }
    if (actionState === "starting") return "Starting session…";
    return "Start session";
  }, [actionState, supportsAiSharing, isGallery]);

  // The choice is "can people edit, or only view?" — the same question for a
  // cloud drawing and a scene-only room, so it is never gated on where the
  // drawing is stored. Whoever can share is offered it. See ux-rules.md § 15a.
  const showRoleSelector = canCreateShareLink;

  const stopCopy = STOP_SESSION_COPY[stopCase];

  const handleStart = async () => {
    setStartError(null);
    setStartSettled(false);
    setActionState("starting");
    try {
      await onStartSession();
      setStartSettled(true);
      if (isGallery) setActionState("idle");
    } catch (error) {
      console.error("[CollabShareDialog] Failed to start session:", error);
      setStartError(
        isGallery
          ? "The sharing link could not be prepared. Please try again."
          : "The collaboration session could not be started. Please try again.",
      );
      setActionState((current) => (current === "starting" ? "idle" : current));
    }
  };

  // Starting is not finished when onStartSession resolves. It returns once the
  // WebSocket is created, so the session becomes active and its link becomes
  // known several renders later. Clearing "starting" on resolution therefore
  // flashed the button back to "Start session", then swapped the whole dialog
  // to a "Preparing room link…" placeholder that immediately replaced itself.
  // Hold the entry view until the share view has something real to show.
  useEffect(() => {
    if (actionState !== "starting" || !startSettled) return;

    // Active and either the link is ready or this viewer gets no link at all.
    if (isCollaborating && (!canCreateShareLink || (shareUrl && !linkBusy))) {
      setActionState("idle");
      return;
    }

    // Safety valve only. connect() swallows its own failures, so a session that
    // never activates would otherwise leave the button stuck on "Starting
    // session…". This must not fire on a healthy start.
    const timer = setTimeout(() => setActionState("idle"), START_SETTLE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [actionState, startSettled, isCollaborating, canCreateShareLink, shareUrl, linkBusy]);

  const handleStop = async (choice: StopSessionChoice) => {
    setStopError(null);
    setActionState("stopping");
    try {
      await onStopSession(choice);
      setShowStopConfirm(false);
      onClose();
    } catch (error) {
      console.error("[CollabShareDialog] Failed to stop session:", error);
      setStopError("You could not leave the collaboration session. Please try again.");
    } finally {
      setActionState("idle");
    }
  };

  const handleCopy = async () => {
    try {
      await onCopyLink();
      setCopyState("copied");
    } catch {
      setCopyState("error");
    }
  };

  const handleCopyAiCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setAiCopyState("copied");
    } catch {
      setAiCopyState("error");
    }
  };

  const handleShareWithAi = async () => {
    if (!onShareWithAi || aiRequestPending.current) return;
    aiRequestPending.current = true;
    setPreparingAi(true);
    setAiShareError(null);
    try {
      await onShareWithAi();
    } catch {
      setAiShareError(
        isExistingAccessLink
          ? "AI access could not be prepared. Please try again."
          : "AI access could not be prepared. Please try Share with AI again.",
      );
    } finally {
      aiRequestPending.current = false;
      setPreparingAi(false);
    }
  };

  return (
    <div
      style={overlayStyle}
      onClick={showStopConfirm ? undefined : onClose}
      role="dialog"
      data-app-usage-surface="sharing"
      aria-modal={isGallery}
      aria-label={dialogTitle}
    >
      <div
        ref={dialogRef}
        tabIndex={isGallery ? -1 : undefined}
        style={dialogStyle}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (!isGallery || event.key !== "Tab") return;
          const controls = event.currentTarget.querySelectorAll<HTMLElement>(
            ":is(button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), a[href], [tabindex='0'])",
          );
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (!first) {
            event.preventDefault();
          } else if (
            event.shiftKey &&
            (event.target === first || event.target === event.currentTarget)
          ) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && event.target === last) {
            event.preventDefault();
            first.focus();
          }
        }}
      >
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>{dialogTitle}</h2>
          {showDrawingIdentity && drawingTitle && (
            <p style={{ ...identityStyle, overflowWrap: "anywhere" }}>{drawingTitle}</p>
          )}
          {!isGallery && accessLoading ? (
            <p role="status" style={sectionSubtleStyle}>
              Checking sharing access…
            </p>
          ) : !isGallery && accessError ? (
            <>
              <p role="alert" style={sectionSubtleStyle}>
                {accessError}
              </p>
              <Button onClick={onRetryAccess} style={{ marginTop: 16 }}>
                Retry
              </Button>
            </>
          ) : checkingEntryAccess ? (
            <p role="status" style={sectionSubtleStyle}>
              {supportsAiSharing ? "Checking people and AI access…" : "Checking people access…"}
            </p>
          ) : ((!isGallery && (!isCollaborating || actionState === "starting")) ||
              (isGallery && canCreateShareLink && !shareUrl && !linkBusy)) &&
            !hasActiveAiAccess &&
            !isExistingAccessLink &&
            !sharedWithPeople &&
            !shareUrl &&
            !preparingAi &&
            !peopleAccessLoading &&
            !peopleAccessError &&
            !aiAccessLoading &&
            !aiAccessError ? (
            <>
              {!isGallery && (
                <>
                  <p style={sectionBodyStyle}>
                    {supportsAiSharing
                      ? "Work together on this drawing with people or your AI assistant."
                      : sceneMode === "file-backed"
                        ? canCreateShareLink
                          ? "Start a live collaboration session for this cloud drawing."
                          : "Start or join live collaboration for this cloud drawing."
                        : "Start a private live collaboration session for this drawing."}
                  </p>
                  <p style={sectionSubtleStyle}>
                    {supportsAiSharing && canCreateShareLink
                      ? "Share a link with people, or give your assistant a code to join and work on this drawing."
                      : sceneMode === "file-backed"
                        ? canCreateShareLink
                          ? "After the session starts, copy its link to invite signed-in people to collaborate on the same drawing."
                          : "Starting a session does not change who can access the drawing. Only the owner can create or copy its sharing link."
                        : "After the session starts, copy its room link to invite signed-in people in your organization. The session is temporary and ends after the last participant leaves."}
                  </p>
                </>
              )}
              <div style={buttonRowStyle}>
                {(!supportsAiSharing || canCreateShareLink) && (
                  <Button
                    variant="primary"
                    onClick={() => void handleStart()}
                    disabled={startSessionDisabled || actionState === "starting" || aiLoading}
                    title={startSessionDisabled ? startSessionDisabledReason : undefined}
                  >
                    {sessionLabel}
                  </Button>
                )}
                {supportsAiSharing && (
                  <Button
                    variant="primary"
                    onClick={() => void handleShareWithAi()}
                    disabled={actionState === "starting" || aiLoading}
                  >
                    {aiShareError || aiError ? "Retry" : "Share with AI"}
                  </Button>
                )}
              </div>
              {localChoice && (
                <p style={sectionSubtleStyle}>
                  <button
                    type="button"
                    onClick={saveToCloudForAiDisabled ? undefined : onSaveToCloudForAi}
                    disabled={saveToCloudForAiDisabled}
                    style={
                      saveToCloudForAiDisabled
                        ? { ...aiHelpStyle, cursor: "not-allowed", opacity: 0.6 }
                        : aiHelpStyle
                    }
                  >
                    Want a permanent link and AI assistant access? Save to cloud
                  </button>
                  {saveToCloudForAiDisabled && saveToCloudForAiReason
                    ? ` ${saveToCloudForAiReason}`
                    : null}
                </p>
              )}
            </>
          ) : (
            <>
              {!isGallery && !isCollaborating && hasActiveAiAccess && (
                <p role="status" style={sectionSubtleStyle}>
                  Connecting to live collaboration. Your AI access is already set up.
                </p>
              )}
              {showPeopleLink ? (
                <>
                  {isExistingAccessLink && (
                    <>
                      <label htmlFor="existing-access-link" style={labelStyle}>
                        People with existing access
                      </label>
                      <p
                        id="existing-access-link-description"
                        style={{
                          ...sectionSubtleStyle,
                          alignSelf: "stretch",
                          textAlign: "left",
                          marginTop: 0,
                        }}
                      >
                        Only people who already have access can open this link.
                      </p>
                    </>
                  )}
                  {showRoleSelector && (
                    <div style={roleRowStyle}>
                      <span style={roleLabelStyle} id="share-link-role-label">
                        People with this link
                      </span>
                      <div
                        style={segmentedStyle}
                        role="group"
                        aria-labelledby="share-link-role-label"
                      >
                        {ROLE_OPTIONS.map((option) => {
                          const selected = linkRole === option.role;
                          return (
                            <button
                              key={option.role}
                              type="button"
                              aria-pressed={selected}
                              disabled={linkBusy || peopleAccessLoading || !!peopleAccessError}
                              onClick={() => {
                                if (!selected) onLinkRoleChange?.(option.role);
                              }}
                              style={selected ? segmentSelectedStyle : segmentStyle}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {!supportsAiSharing && <label style={labelStyle}>Link</label>}
                  <div style={supportsAiSharing ? { ...rowStyle, marginTop: 16 } : rowStyle}>
                    <input
                      id={isExistingAccessLink ? "existing-access-link" : undefined}
                      aria-label={isExistingAccessLink ? "Drawing link" : "Sharing link"}
                      aria-describedby={
                        isExistingAccessLink ? "existing-access-link-description" : undefined
                      }
                      readOnly
                      value={
                        linkBusy
                          ? "Preparing link…"
                          : (shareUrl ??
                            (sceneMode === "scene-only"
                              ? "Preparing room link…"
                              : "Preparing file link…"))
                      }
                      style={inputStyle}
                    />
                    <Button
                      variant="primary"
                      style={sceneMode === "file-backed" ? copyButtonStyle : undefined}
                      onClick={() => void handleCopy()}
                      disabled={linkBusy || peopleAccessLoading || !!peopleAccessError || !shareUrl}
                    >
                      {copyState === "copied"
                        ? "Copied!"
                        : copyState === "error"
                          ? "Retry"
                          : "Copy link"}
                    </Button>
                  </div>
                  {shareUrl && !linkBusy && (
                    <>
                      {copyState === "error" && (
                        <p role="alert" style={sectionSubtleStyle}>
                          {isExistingAccessLink
                            ? "Copy failed. Select and copy the drawing link above manually."
                            : "Copy failed. Select and copy the sharing link above manually."}
                        </p>
                      )}
                      {!isExistingAccessLink && (
                        <div style={qrWrapperStyle}>
                          <QRCodeSVG value={shareUrl} size={160} />
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : peopleAccessLoading ? (
                <p role="status" style={sectionSubtleStyle}>
                  Checking people access…
                </p>
              ) : peopleAccessError ? (
                <>
                  <p role="alert" style={sectionSubtleStyle}>
                    {peopleAccessError}
                  </p>
                  <Button onClick={onRetryPeopleAccess}>{peopleAccessRetryLabel}</Button>
                </>
              ) : !canCreateShareLink ? (
                <p style={sectionSubtleStyle}>
                  {isGallery
                    ? "Only the drawing owner can create or copy its sharing link."
                    : sceneMode === "file-backed"
                      ? "Only the drawing owner can create or copy its sharing link. You can still collaborate while this session is active."
                      : "Only the person who started this session can share its link. You can still collaborate while it is active."}
                </p>
              ) : sharedWithPeople ? (
                <p style={sectionSubtleStyle}>
                  No active sharing link is available. Existing access is unchanged.
                </p>
              ) : null}
              {showPeopleLink && peopleAccessLoading && (
                <p role="status" style={sectionSubtleStyle}>
                  Checking people access…
                </p>
              )}
              {showPeopleLink && peopleAccessError && (
                <>
                  <p role="alert" style={sectionSubtleStyle}>
                    {peopleAccessError}
                  </p>
                  <Button onClick={onRetryPeopleAccess}>{peopleAccessRetryLabel}</Button>
                </>
              )}
              {(hasActiveAiAccess || preparingAi) && (
                <>
                  <div
                    style={{
                      ...roleRowStyle,
                      position: "relative",
                      marginTop: 28,
                      justifyContent: "flex-start",
                      gap: 12,
                    }}
                    data-testid="share-ai-access"
                  >
                    <span style={roleLabelStyle}>AI assistant</span>
                    <span
                      ref={aiPermissionTooltipRef}
                      style={permissionTooltipContainerStyle}
                      onMouseEnter={() => {
                        setAiPermissionHovered(true);
                        setAiPermissionDismissed(false);
                      }}
                      onMouseLeave={() => setAiPermissionHovered(false)}
                      onFocusCapture={() => {
                        setAiPermissionFocused(true);
                        setAiPermissionDismissed(false);
                      }}
                      onBlurCapture={(event) => {
                        if (!event.currentTarget.contains(event.relatedTarget)) {
                          setAiPermissionFocused(false);
                        }
                      }}
                    >
                      <output
                        aria-label="AI permission (read only)"
                        aria-describedby={aiPermissionTooltipId}
                        style={segmentedStyle}
                      >
                        <span
                          style={
                            aiAccess ? segmentSelectedStyle : { ...segmentStyle, cursor: "default" }
                          }
                        >
                          {aiPermissionCopy.badge}
                        </span>
                      </output>
                      <button
                        type="button"
                        aria-label="About AI permission"
                        aria-describedby={aiPermissionTooltipId}
                        aria-expanded={aiPermissionTooltipOpen}
                        style={permissionTooltipButtonStyle}
                        onClick={() => {
                          setAiPermissionPinned((pinned) => {
                            setAiPermissionDismissed(pinned);
                            return !pinned;
                          });
                        }}
                      >
                        <svg
                          viewBox="0 0 16 16"
                          width="16"
                          height="16"
                          aria-hidden="true"
                          focusable="false"
                        >
                          <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" />
                          <path
                            d="M8 7.1v4M8 4.8h.01"
                            fill="none"
                            stroke="currentColor"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                      <span
                        id={aiPermissionTooltipId}
                        role="tooltip"
                        aria-hidden={!aiPermissionTooltipOpen}
                        style={{
                          ...permissionTooltipStyle,
                          ...(aiPermissionTooltipOpen
                            ? permissionTooltipOpenStyle
                            : permissionTooltipClosedStyle),
                        }}
                      >
                        {aiPermissionCopy.sentence}
                      </span>
                    </span>
                  </div>
                  <p style={aiPromptLabelStyle}>Paste this into GitHub Copilot</p>
                  <div style={{ ...rowStyle, alignItems: "center" }}>
                    <textarea
                      id="collab-ai-prompt"
                      ref={aiPromptRef}
                      aria-label="AI prompt"
                      readOnly
                      rows={1}
                      style={aiPromptStyle}
                      data-testid="share-ai-prompt"
                      value={
                        aiPrompt ?? (preparingAi ? "Preparing AI prompt…" : "Loading AI prompt…")
                      }
                    />
                    <Button
                      variant="primary"
                      style={copyButtonStyle}
                      disabled={!aiPrompt}
                      onClick={aiPrompt ? () => void handleCopyAiCode(aiPrompt) : undefined}
                    >
                      {aiCopyState === "copied" ? "Copied!" : "Copy prompt"}
                    </Button>
                  </div>
                  {hasActiveAiAccess &&
                    !isGallery &&
                    (aiConnectionStatus === "connected" || aiConnectionStatus === "idle") && (
                      <p style={sectionSubtleStyle} role="status">
                        {aiConnectionStatus === "connected"
                          ? "Your AI is connected to this drawing."
                          : "Your AI is idle."}
                      </p>
                    )}
                  {preparingAi && (
                    <p style={sectionSubtleStyle} role="status">
                      Preparing AI code…
                    </p>
                  )}
                  {aiCopyState === "error" && (
                    <p role="alert" style={sectionSubtleStyle}>
                      Copy failed. Select and copy the AI prompt above manually.
                    </p>
                  )}
                  {hasActiveAiAccess && onConnectAiAssistant && (
                    <p style={sectionSubtleStyle}>
                      <button type="button" onClick={onConnectAiAssistant} style={aiHelpStyle}>
                        First time? Connect an AI assistant
                      </button>
                    </p>
                  )}
                </>
              )}
              {!hasActiveAiAccess && !preparingAi && aiAccessLoading && (
                <p role="status" style={sectionSubtleStyle}>
                  Checking AI access…
                </p>
              )}
              {aiAccessError && (
                <>
                  <p role="alert" style={sectionSubtleStyle}>
                    {aiAccessError}
                  </p>
                  <Button onClick={onRetryAiAccess}>Retry AI access</Button>
                </>
              )}
              {!isGallery && isCollaborating && <p style={sectionSubtleStyle}>{stopCopy.line}</p>}
              {needsSessionRestart && (
                <p role="status" style={sectionSubtleStyle}>
                  You are not in a live session. Start a session to collaborate in real time.
                </p>
              )}
              <div style={buttonRowStyle}>
                {needsSessionRestart && (
                  <Button
                    variant="primary"
                    disabled={
                      startSessionDisabled || preparingAi || aiLoading || actionState === "starting"
                    }
                    title={startSessionDisabled ? startSessionDisabledReason : undefined}
                    onClick={() => void handleStart()}
                  >
                    {actionState === "starting" ? "Starting session…" : "Start session"}
                  </Button>
                )}
                {!showPeopleLink && canPreparePeopleLink && (
                  <Button
                    variant="primary"
                    disabled={startSessionDisabled || preparingAi || actionState === "starting"}
                    title={startSessionDisabled ? startSessionDisabledReason : undefined}
                    onClick={() => void handleStart()}
                  >
                    {sessionLabel}
                  </Button>
                )}
                {supportsAiSharing && !hasActiveAiAccess && !aiAccessLoading && !aiAccessError && (
                  <Button
                    variant="primary"
                    disabled={preparingAi || aiLoading || (isExistingAccessLink && !aiAccess)}
                    onClick={() => void handleShareWithAi()}
                  >
                    {preparingAi
                      ? "Preparing AI code…"
                      : aiShareError || aiError
                        ? isExistingAccessLink
                          ? "Retry AI sharing"
                          : "Retry"
                        : "Share with AI"}
                  </Button>
                )}
                {supportsAiSharing && aiCode && onRequestStopAiAccess && (
                  <Button variant="danger" disabled={preparingAi} onClick={onRequestStopAiAccess}>
                    Stop AI access
                  </Button>
                )}
                {!isGallery && isCollaborating && (
                  <Button
                    variant="danger"
                    disabled={preparingAi || actionState === "starting"}
                    onClick={() => {
                      setStopError(null);
                      setShowStopConfirm(true);
                    }}
                  >
                    Leave session
                  </Button>
                )}
              </div>
            </>
          )}
          {startError && (
            <p role="alert" style={sectionSubtleStyle}>
              {startError}
            </p>
          )}
          {linkError && (
            <p role="alert" style={sectionSubtleStyle}>
              {linkError}
            </p>
          )}
          {aiShareError && (
            <p role="alert" style={sectionSubtleStyle}>
              {aiShareError}
            </p>
          )}
          {aiLoading && !checkingEntryAccess && (
            <p role="status" style={sectionSubtleStyle}>
              Checking AI access…
            </p>
          )}
          {aiError && !aiShareError && (
            <p role="alert" style={sectionSubtleStyle}>
              {aiError}
            </p>
          )}
        </div>
      </div>

      {showStopConfirm && (
        <DestructiveConfirmationDialog
          title={stopCopy.title}
          confirmLabel={stopCopy.confirmLabel}
          busyLabel="Leaving…"
          busy={actionState === "stopping"}
          error={stopError}
          blocking
          testId="stop-session-dialog"
          alternateLabel={stopCopy.alternateLabel}
          onAlternate={stopCopy.alternateLabel ? () => void handleStop("keep-browser") : undefined}
          neutralConfirm={!!stopCopy.alternateLabel}
          onClose={() => setShowStopConfirm(false)}
          onConfirm={() => void handleStop(stopCopy.alternateLabel ? "keep-session" : "default")}
        >
          {stopCopy.body.map((paragraph) => (
            <p key={paragraph} style={{ margin: 0 }}>
              {paragraph}
            </p>
          ))}
        </DestructiveConfirmationDialog>
      )}
    </div>
  );
}

const ROLE_OPTIONS: ReadonlyArray<{ role: DrawingAccessRole; label: string }> = [
  { role: "editor", label: "Can edit" },
  { role: "viewer", label: "Can view" },
];

/**
 * How long the entry view waits for a started session to produce a link before
 * giving up and showing the share view anyway. Only reached when the session
 * fails to activate — a healthy start resolves in well under this.
 */
const START_SETTLE_TIMEOUT_MS = 10000;

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 10000,
  background: "var(--exms-overlay-bg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};

const dialogStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 720,
  maxHeight: "calc(100dvh - 104px)",
  overflowY: "auto",
  background: "var(--island-bg-color)",
  borderRadius: 16,
  border: "1px solid var(--dialog-border-color)",
  boxShadow: "var(--exms-shadow-modal)",
  padding: 28,
  fontFamily:
    "Assistant, system-ui, BlinkMacSystemFont, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  color: "var(--text-primary-color)",
};

const sectionStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 20,
  fontWeight: 700,
  color: "var(--exms-link-color)",
};

const sectionBodyStyle: React.CSSProperties = {
  marginTop: 20,
  marginBottom: 0,
  fontSize: 16,
};

const identityStyle: React.CSSProperties = {
  marginTop: 20,
  fontSize: 16,
  fontWeight: 600,
};

const sectionSubtleStyle: React.CSSProperties = {
  marginTop: 16,
  marginBottom: 0,
  fontSize: 15,
  color: "var(--color-text-secondary)",
  lineHeight: 1.5,
};

const buttonRowStyle: React.CSSProperties = {
  marginTop: 24,
  display: "flex",
  justifyContent: "center",
  gap: 12,
  flexWrap: "wrap",
};

const aiHelpStyle: React.CSSProperties = {
  padding: 0,
  border: "none",
  background: "transparent",
  color: "var(--exms-link-color)",
  textDecoration: "underline",
  font: "inherit",
  cursor: "pointer",
};

const aiPromptLabelStyle: React.CSSProperties = {
  alignSelf: "stretch",
  margin: "16px 0 8px",
  fontSize: 14,
  fontWeight: 600,
  textAlign: "left",
};

const aiPromptStyle: React.CSSProperties = {
  alignSelf: "stretch",
  boxSizing: "border-box",
  flex: 1,
  minWidth: 0,
  margin: 0,
  padding: "12px 14px",
  borderRadius: 10,
  border: "1px solid var(--dialog-border-color)",
  background: "var(--input-bg-color)",
  color: "var(--text-primary-color)",
  fontFamily: "Consolas, ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 13,
  lineHeight: 1.5,
  textAlign: "left",
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
  resize: "none",
};

const labelStyle: React.CSSProperties = {
  alignSelf: "stretch",
  marginTop: 20,
  marginBottom: 8,
  fontSize: 14,
  fontWeight: 600,
  textAlign: "left",
};

const rowStyle: React.CSSProperties = {
  width: "100%",
  display: "flex",
  gap: 12,
};

const copyButtonStyle: React.CSSProperties = {
  width: 120,
  flexShrink: 0,
  alignSelf: "center",
};

const roleRowStyle: React.CSSProperties = {
  alignSelf: "stretch",
  marginTop: 20,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
};

const roleLabelStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  textAlign: "left",
};

const segmentedStyle: React.CSSProperties = {
  flex: "none",
  display: "inline-flex",
  padding: 2,
  gap: 2,
  borderRadius: 999,
  border: "1px solid var(--dialog-border-color)",
  background: "var(--color-surface-low)",
};

const segmentStyle: React.CSSProperties = {
  padding: "7px 16px",
  border: "none",
  borderRadius: 999,
  background: "transparent",
  color: "var(--color-text-secondary)",
  fontFamily: "inherit",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  cursor: "pointer",
};

const segmentSelectedStyle: React.CSSProperties = {
  ...segmentStyle,
  background: "var(--color-primary-light)",
  color: "var(--color-on-primary-soft)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.14)",
  cursor: "default",
};

const permissionTooltipContainerStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const permissionTooltipButtonStyle: React.CSSProperties = {
  width: 24,
  height: 24,
  padding: 4,
  border: "none",
  borderRadius: 999,
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  lineHeight: 0,
};

const permissionTooltipStyle: React.CSSProperties = {
  position: "absolute",
  zIndex: 1,
  top: "calc(100% + 8px)",
  left: 0,
  width: "min(320px, 100%)",
  boxSizing: "border-box",
  padding: "10px 12px",
  border: "1px solid var(--dialog-border-color)",
  borderRadius: 8,
  background: "var(--island-bg-color)",
  color: "var(--text-primary-color)",
  boxShadow: "var(--exms-shadow-modal)",
  fontSize: 13,
  fontWeight: 400,
  lineHeight: 1.4,
  textAlign: "left",
};

const permissionTooltipOpenStyle: React.CSSProperties = {
  visibility: "visible",
  opacity: 1,
};

const permissionTooltipClosedStyle: React.CSSProperties = {
  visibility: "hidden",
  opacity: 0,
  pointerEvents: "none",
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: "12px 14px",
  borderRadius: 10,
  border: "1px solid var(--dialog-border-color)",
  background: "var(--input-bg-color)",
  color: "var(--text-primary-color)",
  fontSize: 15,
};

const qrWrapperStyle: React.CSSProperties = {
  marginTop: 24,
  padding: 16,
  border: "1px solid var(--dialog-border-color)",
  borderRadius: 10,
  background: "var(--exms-media-bg)",
};
